// src/pages/Login.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { getAuthErrorMessage } from '@/lib/authMessages';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { supabase } from '@/lib/supabase';
import logo from '/media/Blue_Logo_Animated.mp4';
import background from '/media/background.png';
import BiometricLogin from '@/components/auth/BiometricLogin';

const DEFAULT_RECRUITMENT_ORIGINS = [
  'https://rekrutacja.kconnect.pl',
  'https://kconnect-recruitment.pages.dev',
];

const getAllowedRecruitmentOrigins = () => {
  const configured = import.meta.env.VITE_RECRUITMENT_ALLOWED_ORIGINS;
  if (!configured || typeof configured !== 'string') {
    return DEFAULT_RECRUITMENT_ORIGINS;
  }

  return configured
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const isAllowedRecruitmentOrigin = (origin) => {
  const allowedOrigins = getAllowedRecruitmentOrigins();
  return allowedOrigins.includes(origin);
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const { signIn, isLoading, authError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if biometric is available on this device
  useEffect(() => {
    const checkBiometric = async () => {
      try {
        if (window.PublicKeyCredential) {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricAvailable(available);
        }
      } catch (e) {
        logger.debug('Biometric check failed:', e);
      }
    };
    checkBiometric();
  }, []);

  const redirectAfterAuth = async () => {
    const nextParam = searchParams.get('next');
    if (!nextParam) {
      navigate('/');
      return;
    }

    let nextUrl;
    try {
      nextUrl = new URL(nextParam);
    } catch (_) {
      navigate('/');
      return;
    }

    if (!isAllowedRecruitmentOrigin(nextUrl.origin)) {
      logger.warn('Blocked untrusted next redirect origin:', nextUrl.origin);
      navigate('/');
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token || !session?.refresh_token) {
      navigate('/');
      return;
    }

    const hashParams = new URLSearchParams();
    hashParams.set('access_token', session.access_token);
    hashParams.set('refresh_token', session.refresh_token);
    hashParams.set('token_type', session.token_type || 'bearer');
    if (session.expires_in) {
      hashParams.set('expires_in', String(session.expires_in));
    }

    const nextPath = `${nextUrl.pathname}${nextUrl.search}`;
    window.location.href = `${nextUrl.origin}${nextPath}#${hashParams.toString()}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await signIn(email, password);
      await redirectAfterAuth();
    } catch (error) {
      logger.error('Auth error:', error);
    }
  };

  const handleBiometricSuccess = async () => {
    await redirectAfterAuth();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${background})` }}
      />
      {/* White overlay for softer colors */}
      <div className="absolute inset-0 bg-white/60" />

      <Card className="w-full max-w-md relative z-10 shadow-xl">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <video
              src={logo}
              autoPlay
              loop
              muted
              playsInline
              className="h-24 w-24 object-contain"
            />
          </div>
          <CardTitle className="text-2xl text-center">
            Witaj w KConnect
          </CardTitle>
        </CardHeader>
        <CardContent>
          {authError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {getAuthErrorMessage(authError)}
              {authError.toLowerCase().includes('zablokowane') && (
                <div className="mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Kliknij tutaj, aby zresetować hasło
                  </Link>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Adres e-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="twoj@email.com"
                className="mt-1 h-11 rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="password">Haslo</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1 h-11 rounded-xl"
                minLength={6}
              />
              <div className="text-right mt-1">
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-600 hover:underline"
                >
                  Zapomniałeś hasła?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
            >
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Zaloguj
            </Button>
          </form>

          {/* Biometric Login Option */}
          {biometricAvailable && (
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">lub</span>
                </div>
              </div>
              <div className="mt-4">
                <BiometricLogin
                  email={email}
                  onSuccess={handleBiometricSuccess}
                  onError={(error) => logger.error('Biometric login error:', error)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
